import React from "react";
import { useI18n } from "~/i18n/i18n";

export interface ChannelMetrics {
  plays: number;
  revenue: number;
  conversion: number;
  orders: number;
}

interface ChannelSplitCardsProps {
  widget: ChannelMetrics;
  checkout: ChannelMetrics;
  /** Store's primary currency (ISO 4217, e.g. "USD", "NOK"). Defaults to USD. */
  currencyCode?: string;
}

type ChannelKey = "widget" | "checkout";

const METRIC_ICONS = {
  plays: "🎮",
  revenue: "💰",
  conversion: "📈",
  orders: "🏷️",
} as const;

function isEmpty(m: ChannelMetrics): boolean {
  return !m.plays && !m.revenue && !m.conversion && !m.orders;
}

const ChannelSplitCards: React.FC<ChannelSplitCardsProps> = ({ widget, checkout, currencyCode = "USD" }) => {
  const { t, locale } = useI18n();
  const isRTL = locale === "he";

  // Format revenue in the store's native currency (falls back to a bare number if
  // the currency code is somehow invalid for Intl).
  const fmtRevenue = (amount: number): string => {
    const rounded = Math.round(amount || 0);
    try {
      return new Intl.NumberFormat(locale || "en", {
        style: "currency",
        currency: currencyCode,
        maximumFractionDigits: 0,
      }).format(rounded);
    } catch {
      return rounded.toLocaleString();
    }
  };

  const totalPlays = (widget.plays || 0) + (checkout.plays || 0);
  const totalRevenue = (widget.revenue || 0) + (checkout.revenue || 0);

  // Winner = strictly higher revenue. Tie or both zero ⇒ no badge.
  let winner: ChannelKey | null = null;
  if ((widget.revenue || 0) > (checkout.revenue || 0)) winner = "widget";
  else if ((checkout.revenue || 0) > (widget.revenue || 0)) winner = "checkout";

  const cards: Array<{ key: ChannelKey; metrics: ChannelMetrics; accent: string; tint: string }> = [
    { key: "widget", metrics: widget, accent: "#5b5cf6", tint: "#ecebff" },
    { key: "checkout", metrics: checkout, accent: "#ec4899", tint: "#fde9f3" },
  ];

  const fmtMetric = (key: keyof ChannelMetrics, m: ChannelMetrics): string => {
    switch (key) {
      case "revenue":
        return fmtRevenue(m.revenue || 0);
      case "conversion":
        return `${(m.conversion || 0).toFixed(1)}%`;
      default:
        return Number(m[key] || 0).toLocaleString();
    }
  };

  return (
    <div className="mb-8">
      <div className={`flex items-end justify-between gap-4 mb-4 flex-wrap ${isRTL ? "flex-row-reverse" : ""}`}>
        <h3 className="m-0 font-display text-[19px] font-bold text-ink">{t("dashboard.channels.sectionTitle")}</h3>
        <div className={`flex items-center gap-5 text-[13px] ${isRTL ? "flex-row-reverse" : ""}`}>
          <span className="text-muted font-semibold">
            {t("dashboard.channels.totalPlays")}:{" "}
            <span className="text-ink font-extrabold">{totalPlays.toLocaleString()}</span>
          </span>
          <span className="text-muted font-semibold">
            {t("dashboard.channels.totalRevenue")}:{" "}
            <span className="text-ink font-extrabold">{fmtRevenue(totalRevenue)}</span>
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[18px]">
        {cards.map(({ key, metrics, accent, tint }) => {
          const empty = isEmpty(metrics);
          const isWinner = winner === key;
          return (
            <div
              key={key}
              className="relative bg-white rounded-card p-[22px] shadow-soft transition-all hover:-translate-y-1 hover:shadow-lift"
              style={isWinner ? { boxShadow: `0 0 0 2px ${accent}, 0 8px 24px ${accent}22` } : undefined}
            >
              {/* Header: icon dot + title + subtitle */}
              <div className={`flex items-start gap-3 ${isRTL ? "flex-row-reverse text-right" : ""}`}>
                <div
                  className="flex-none w-[42px] h-[42px] rounded-[13px] flex items-center justify-center text-[20px]"
                  style={{ background: tint }}
                >
                  {key === "widget" ? "🛍️" : "✅"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[16px] font-bold text-ink truncate">{t(`dashboard.channels.${key}.title`)}</div>
                  <div className="text-[12.5px] text-muted font-semibold mt-0.5">
                    ▸ {t(`dashboard.channels.${key}.subtitle`)}
                  </div>
                  {empty && (
                    <div className="text-[12px] text-faint font-semibold mt-1">{t("dashboard.channels.noDataYet")}</div>
                  )}
                </div>
                {isWinner && (
                  <span
                    className="flex-none text-[11px] font-extrabold px-2.5 py-1 rounded-full whitespace-nowrap"
                    style={{ color: accent, background: tint }}
                  >
                    {t("dashboard.channels.bestForSales")} ★
                  </span>
                )}
              </div>

              {/* Metric rows */}
              <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
                {(Object.keys(METRIC_ICONS) as Array<keyof ChannelMetrics>).map((mk) => (
                  <div key={mk} className={`flex items-center gap-2 ${isRTL ? "flex-row-reverse text-right" : ""}`}>
                    <span className="text-[16px] leading-none">{METRIC_ICONS[mk]}</span>
                    <div className="min-w-0">
                      <div className="font-display text-[18px] font-bold text-ink leading-tight">
                        {fmtMetric(mk, metrics)}
                      </div>
                      <div className="text-[11.5px] text-muted font-semibold">{t(`dashboard.channels.metrics.${mk}`)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChannelSplitCards;
